import {
  BannerRole as ApiBannerRole,
  BannerStatus as ApiBannerStatus,
  BannerFragment,
} from "@/graphql";
import { mapToSport } from "@/mappers/sport.mappers";
import { Banner, BannerRole, BannerStatus } from "@/types";

export const mapToBanner = (fragment: BannerFragment): Banner => ({
  id: fragment.id,
  headLine: fragment.headLine,
  subHeadLine: fragment.subHeadLine ?? null,
  buttonText: fragment.buttonText,
  buttonLink: fragment.buttonLink,
  primaryColor: fragment.primaryColor,
  status: mapToBannerStatus(fragment.status),
  sports: fragment.sports?.map(mapToSport) ?? [],
  roles: fragment.roles.map(mapToBannerRole),
  legacyImages: fragment.legacyImages,
  imageFiles:
    fragment.imageFiles?.map((image) => ({
      id: image.id,
      url: image.url ?? null,
    })) ?? [],
  sortingPosition: fragment.sortingPosition ?? null,
});

const bannerRoleMap: { [key in ApiBannerRole]: BannerRole } = {
  [ApiBannerRole.Athlete]: "athlete",
  [ApiBannerRole.FederationMember]: "federationMember",
  [ApiBannerRole.ClubOwner]: "clubOwner",
  [ApiBannerRole.ClubManager]: "clubManager",
  [ApiBannerRole.Referee]: "referee",
  [ApiBannerRole.TournamentOrganizer]: "tournamentOrganizer",
  [ApiBannerRole.SportFan]: "sportFan",
  [ApiBannerRole.Guest]: "guest",
  [ApiBannerRole.Other]: "other",
};

export const mapToBannerRole = (role: ApiBannerRole): BannerRole => {
  return bannerRoleMap[role];
};

const bannerRoleMapReverse: { [key in BannerRole]: ApiBannerRole } = {
  athlete: ApiBannerRole.Athlete,
  federationMember: ApiBannerRole.FederationMember,
  clubOwner: ApiBannerRole.ClubOwner,
  clubManager: ApiBannerRole.ClubManager,
  referee: ApiBannerRole.Referee,
  tournamentOrganizer: ApiBannerRole.TournamentOrganizer,
  sportFan: ApiBannerRole.SportFan,
  guest: ApiBannerRole.Guest,
  other: ApiBannerRole.Other,
};

export const mapToApiBannerRole = (role: BannerRole): ApiBannerRole => {
  return bannerRoleMapReverse[role];
};

const bannerStatusMap: { [key in ApiBannerStatus]: BannerStatus } = {
  [ApiBannerStatus.Active]: "active",
  [ApiBannerStatus.Inactive]: "inactive",
};

export const mapToBannerStatus = (status: ApiBannerStatus): BannerStatus => {
  return bannerStatusMap[status];
};

const bannerStatusMapReverse: { [key in BannerStatus]: ApiBannerStatus } = {
  active: ApiBannerStatus.Active,
  inactive: ApiBannerStatus.Inactive,
};

export const mapToApiBannerStatus = (status: BannerStatus): ApiBannerStatus => {
  return bannerStatusMapReverse[status];
};

/**
 * Outbound button pair for the create / update banner mutations.
 *
 * The "Button" switch box being off means the banner has **no** button, but the
 * current contract has no way to say that: `CreateBannerInputNew.buttonText` /
 * `buttonLink` are non-null `String!` inputs, and `BannerNew.buttonText` /
 * `buttonLink` are non-null outputs — so `null` cannot be sent on create at all,
 * and sending it on update would clear the columns to `NULL`, which the
 * non-nullable output then refuses to serialise (the mutation's own
 * `...banner` selection would fail). The empty string is therefore the only
 * "absent" value that round-trips, and the banners list already renders it as
 * "no button" (`BannersTableDesktop` / `BannersTableMobile` branch on
 * `banner.buttonText`).
 *
 * Values typed before the switch was turned off stay in the form (so toggling
 * back on restores them) — they are dropped here, never submitted.
 *
 * @param values Validated form values carrying the FE-only `buttonEnabled` flag.
 * @returns `{ buttonText, buttonLink }` ready to spread into the mutation input.
 */
export const mapToApiBannerButton = (values: {
  buttonEnabled: boolean;
  buttonText: string;
  buttonLink: string;
}): { buttonText: string; buttonLink: string } =>
  values.buttonEnabled
    ? { buttonText: values.buttonText.trim(), buttonLink: values.buttonLink.trim() }
    : { buttonText: "", buttonLink: "" };
